import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import {
  PieChart,
  buildPieChartSerie,
} from '@delmonicos/shared-design-system-react/src/components/pie-chart/pie-chart';
import { PieChartLegend } from '@delmonicos/shared-design-system-react/src/components/pie-chart/pie-chart-legend';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { PieChartColors } from '@delmonicos/shared-design-system-react/src/models/enums/pie-chart-colors.enum';
import { TransactionReportsStatisticsByConnectorTypeModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/transaction-report/transaction-reports-statistics-by-connector-type.model';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { mapTypeToGenericConnectorType } from '@app/feature/charge-point/helper/connector-type.helper';

export enum ByConnectorTypeCardVariantEnum {
  Transactions = 'transactions',
  Revenue = 'revenue',
  Energy = 'energy',
}

interface ByConnectorTypeVariation {
  name: string;
  titleVariant: AccentVariantEnum;
  digits: number;
  unit?: string;
  transformValue?: (value: number) => number;
}

const byConnectorTypeVariations: Record<
  ByConnectorTypeCardVariantEnum,
  ByConnectorTypeVariation
> = {
  [ByConnectorTypeCardVariantEnum.Transactions]: {
    name: 'transactions',
    titleVariant: AccentVariantEnum.Primary,
    digits: 0,
  },
  [ByConnectorTypeCardVariantEnum.Revenue]: {
    name: 'revenue',
    titleVariant: AccentVariantEnum.Accent1,
    digits: 0,
    unit: '€',
  },
  [ByConnectorTypeCardVariantEnum.Energy]: {
    name: 'energy',
    titleVariant: AccentVariantEnum.Secondary,
    digits: 0,
    unit: 'MWh',
    transformValue: (value) => value / 1000, // Convert from kWh to MWh
  },
};

interface ByConnectorTypeCardProps {
  variant?: ByConnectorTypeCardVariantEnum;
  data?: TransactionReportsStatisticsByConnectorTypeModel;
}

export const ByConnectorTypeCard = ({
  data,
  variant = ByConnectorTypeCardVariantEnum.Transactions,
}: ByConnectorTypeCardProps): React.JSX.Element => {
  const { t } = useTranslation();

  const variation = byConnectorTypeVariations[variant];

  const serie = useMemo(() => {
    if (!data?.value) return [];

    return buildPieChartSerie({
      items: data.value,
      getName: (item) =>
        t(
          `delmonicos-shared-sdk.ocpi-protocol-enums.connector-type-alt.${mapTypeToGenericConnectorType(item.connectorType).name}`,
        ),
      getValue: (item) =>
        variation.transformValue
          ? variation.transformValue(item.total ?? 0)
          : (item.total ?? 0),
      getIcon: (item) => mapTypeToGenericConnectorType(item.connectorType).icon,
      getColor: (_item, index) =>
        // @ts-ignore
        PieChartColors[`SERIE_${(index % 4) + 1}`],
      othersLabel: t('$.statistics.cards.by-connector-type.others'),
      othersIcon: 'wire',
    });
  }, [data, t, variation]);

  return (
    <Card flex={1} gap={GapEnum.M}>
      <AccentTitle
        icon="wire"
        variant={variation.titleVariant}
        text={t('$.statistics.cards.by-connector-type.title')}
      />
      <Stack gap={GapEnum.M} flex={1}>
        <PieChart
          serie={serie}
          chartId={`pie-chart-${variation.name}-by-connector-type`}
        />
        <PieChartLegend
          serie={serie}
          unit={variation.unit}
          digits={variation.digits}
        />
      </Stack>
    </Card>
  );
};
